import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useUserRole } from "components/Sidebar/hooks/useUserRole";
import { TabSwitcher } from "components/ui/TabSwitcher";
import { ReportInsights } from "features/reports/components/ReportInsights/ReportInsights";
import { ReportsFilters } from "features/reports/components/ReportsFilters/ReportsFilters";
import { ReportsTable } from "features/reports/components/ReportsTable/ReportsTable";
import { useExportReports } from "features/reports/hooks/useExportReports";
import { useReports } from "features/reports/hooks/useReports";
import { ROUTES } from "utils/routes";

import { getTabOptions } from "./components/ReportsNavbar/utils/menuItems";
import styles from "./ReportsPage.module.css";

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getDefaultFilters = (reportType) => {
  const today = new Date();
  const twoDaysAgo = new Date(today);
  twoDaysAgo.setDate(today.getDate() - 2);

  const startDate = formatDate(twoDaysAgo);
  const endDate = formatDate(today);

  switch (reportType) {
    case "audit-log":
      return { startDate, endDate };
    case "appointments":
      return { startDate, endDate };
    case "accounting":
      return { consultationStartDate: startDate, consultationEndDate: endDate };
    case "letters":
      return { letterSentStartDate: startDate, letterEndDate: endDate };
    case "investigations":
      return { investigationStartDate: startDate, investigationEndDate: endDate };
    case "prescriptions":
      return { prescribedStartDate: startDate, prescribedEndDate: endDate };
    case "immunisations":
      return { dateGivenStart: startDate, dateGivenEnd: endDate };
    default:
      // Consultations (patients) report
      return { consuiltationStartDate: startDate, consultationEndDate: endDate };
  }
};

const ReportsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(10);
  const [filters, setFilters] = useState({});
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const { reportType } = useParams();
  const { tenantType } = useUserRole();

  const tabOptions = useMemo(
    () => getTabOptions(reportType, tenantType),
    [reportType, tenantType]
  );

  const currentValue = useMemo(() => {
    const path = location.pathname;
    const inTabs = tabOptions.some((tab) => tab.value === path);
    return inTabs ? path : tabOptions[0]?.value ?? ROUTES.GLOBAL.REPORTS;
  }, [location.pathname, tabOptions]);

  const onFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  }, []);

  useEffect(() => {
    setFilters(getDefaultFilters(reportType));
    setPage(1);
  }, [reportType]);

  const { mutate: exportReports, isPending: isExporting } = useExportReports();

  const { items, totalRecords, isLoading } = useReports({
    tenantVal,
    reportType,
    filters,
    page,
    rows,
  });

  return (
    <div className={styles.reportsPageWrapper}>
      <TabSwitcher
        options={tabOptions}
        value={currentValue}
        onChange={(url) => navigate(url)}
        fullWidth
        withOverflow
        marginTop={16}
        marginBottom={24}
      />
      <ReportsFilters
        reportType={reportType}
        filters={filters}
        onFilterChange={onFilterChange}
      />
      <ReportInsights reportType={reportType} filters={filters} tenantVal={tenantVal} />
      <ReportsTable
        reportType={reportType}
        items={items}
        isLoading={isLoading}
        totalRecords={totalRecords}
        page={page}
        onPageChange={setPage}
        rows={rows}
        onRowsChange={setRows}
        onExport={(format) =>
          exportReports({ tenantVal, reportType, filters, format })
        }
        isExporting={isExporting}
        filters={filters}
        onFilterChange={onFilterChange}
      />
    </div>
  );
};

export { ReportsPage };
